import React from 'react';
import { FindASchoolAutocompleteResult, xhrClient } from '../../services/xhrClient';

type BrowseTab = 'location' | 'category';
type CategoryBrowseLinkClickHandler = (link: LocationLink, event: React.MouseEvent<HTMLAnchorElement>) => void;

interface StateLink {
    name: string;
    slug: string;
    abbreviation?: string;
    url?: string;
}

interface LocationLink {
    name: string;
    url: string;
    abbreviation?: string;
    slug?: string;
}

interface CategoryLink {
    subCategoryId: number;
    subCategoryCode: string;
    subCategoryName: string;
}

interface CategoryGroup {
    parentCategory: string;
    parentTreeNodeId: number;
    subCategories: CategoryLink[];
}

interface ProvinceOption {
    label: string;
    value: string;
}

interface SchoolResult {
    isSearchByZip: boolean;
    isSearchByProvince: boolean;
    siteId: number;
    siteCode: string;
    siteName: string;
    siteUrl?: string;
    siteSubTitle?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    phoneNumber?: string;
    phoneNumberExtension?: string;
    emailAddress?: string;
    distanceFromZip?: string;
}

interface SearchResults {
    isSearchByZip: boolean;
    isSearchByProvince: boolean;
    zipCode?: string;
    province?: string;
    currentPage?: number;
    pageSize?: number;
    virtualCount: number;
    schools: SchoolResult[];
}

export interface FindASchoolProps {
    productCode?: string;
    returnUrl?: string;
    showFindASchoolLocator?: boolean;
    enableFindASchoolByName?: boolean;
    autocompleteApiPath?: string;
    categoryStatesApiPath?: string;
    categoryCitiesApiPath?: string;
    browseTab?: BrowseTab;
    zipCode?: string;
    province?: string;
    provinceOptions?: ProvinceOption[];
    stateLinks?: StateLink[];
    selectedStateName?: string;
    cityLinks?: LocationLink[];
    categoryGroups?: CategoryGroup[];
    selectedCategoryId?: number;
    selectedCategoryName?: string;
    categoryStateLinks?: StateLink[];
    selectedCategoryStateName?: string;
    categoryCityLinks?: LocationLink[];
    searchResults?: SearchResults | null;
}

const defaultPageSize = 10;
const pageSizes = [10, 25, 50];
const categoryBrowseBackLoadingDelayMs = 2000;

const styles = `
.find-school-page {
    background: #fff;
    color: #333;
    font-family: "Open Sans", Verdana, Geneva, sans-serif;
    font-size: 14px;
    line-height: 1.4;
}

.find-school-breadcrumb-bar {
    background: #f6f7f8;
    padding: 10px 20px;
}

.find-school-breadcrumbs {
    color: #333;
    font-size: 12px;
    line-height: 1.5;
    margin: 0 auto;
    max-width: 1140px;
    padding: 0 24px;
    white-space: nowrap;
}

.find-school-breadcrumbs a {
    color: #009ddc;
    text-decoration: none;
}

.find-school-breadcrumb-separator {
    color: #000;
    font-size: 10px;
    padding: 0 5px;
    vertical-align: text-bottom;
}

.find-school-shell {
    margin: 0 auto;
    max-width: 1140px;
    padding: 0 20px 30px;
}

.find-school-name-search {
    background: #079fd0;
    color: #fff;
    min-height: 224px;
    padding: 64px 20px 58px;
}

.find-school-name-search-inner {
    margin: 0 auto;
    max-width: 760px;
    position: relative;
    text-align: center;
}

.find-school-name-search-prompt {
    font-size: 16px;
    line-height: 1.4;
    margin: 0 0 18px;
}

.find-school-name-search-field {
    background: #fff;
    border: 1px solid #fff;
    border-radius: 4px;
    box-sizing: border-box;
    display: flex;
    height: 64px;
    position: relative;
    width: 100%;
}

.find-school-name-search-icon {
    align-items: center;
    color: #9a9a9a;
    display: flex;
    flex: 0 0 42px;
    justify-content: center;
    line-height: 1;
    position: relative;
}

.find-school-name-search-icon::before {
    border: 3px solid #9a9a9a;
    border-radius: 50%;
    box-sizing: border-box;
    content: "";
    height: 16px;
    width: 16px;
}

.find-school-name-search-icon::after {
    background: #9a9a9a;
    content: "";
    height: 3px;
    margin-left: -2px;
    margin-top: 14px;
    transform: rotate(45deg);
    width: 10px;
}

.find-school-name-search-input {
    border: 0;
    box-sizing: border-box;
    color: #333;
    flex: 1 1 auto;
    font-size: 16px;
    font-style: italic;
    height: 100%;
    line-height: 1.4;
    min-width: 0;
    outline: 0;
    padding: 0 12px 0 0;
}

.find-school-name-search-input:focus {
    box-shadow: inset 0 0 0 2px #111;
}

.find-school-name-results {
    background: #fff;
    border: 1px solid #cfcfcf;
    border-top: 0;
    box-shadow: 0 2px 2px rgba(0, 0, 0, .08);
    box-sizing: border-box;
    color: #333;
    left: 0;
    list-style: none;
    margin: 0;
    padding: 0;
    position: absolute;
    right: 0;
    text-align: left;
    top: calc(100% - 1px);
    z-index: 20;
}

.find-school-name-results-header {
    background: #f1f1f1;
    color: #1d1d1d;
    font-size: 13px;
    font-weight: 700;
    padding: 8px 12px;
}

.find-school-name-result-item {
    border-top: 1px solid #d7d7d7;
}

.find-school-name-result-link {
    align-items: center;
    color: #004f6e;
    display: flex;
    font-size: 14px;
    gap: 12px;
    justify-content: space-between;
    line-height: 1.4;
    min-height: 42px;
    padding: 8px 12px;
    text-decoration: underline;
}

.find-school-name-results .find-school-name-result-link:hover,
.find-school-name-results .find-school-name-result-link:focus,
.find-school-name-results .find-school-name-result-link:active {
    background: #007bff;
    background-color: #007bff;
    color: #fff;
    text-decoration: none;
}

.find-school-name-result-label {
    overflow-wrap: anywhere;
}

.find-school-name-result-badge {
    border-radius: 12px;
    color: #fff;
    flex: 0 0 auto;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    padding: 4px 10px;
    text-align: center;
    white-space: nowrap;
}

.find-school-name-result-badge-fundamentals {
    background: #2faee4;
}

.find-school-name-result-badge-career-training {
    background: #48c886;
}

.find-school-name-status {
    color: #555;
    font-size: 14px;
    padding: 10px 12px;
}

.find-school-heading,
.find-school-browse-heading {
    color: #333;
    font-family: "Open Sans", Verdana, Geneva, sans-serif;
    font-size: 32px;
    font-weight: 300;
    letter-spacing: 0;
    line-height: 1.2;
    margin: 0;
    padding: 20px 0 0;
}

.find-school-description {
    color: #333;
    display: block;
    font-size: 14px;
    line-height: 1.4;
    margin: 14px 0;
    padding-bottom: 8px;
}

.find-school-locator {
    background: #f6f7f9;
    border: 1px solid #ddd;
    border-radius: 4px;
    display: flex;
    flex-direction: row;
    margin: 20px 0 0;
    padding: 8px;
    width: 100%;
}

.find-school-locator-column {
    display: flex;
    flex-direction: column;
    padding: 40px 0;
    width: 100%;
}

.find-school-locator-column + .find-school-locator-column {
    border-left: 1px solid #ddd;
}

.find-school-form {
    margin: 0 auto;
    width: auto;
}

.find-school-form p {
    margin: 0 0 10px;
}

.find-school-audience {
    color: #1a5d7f;
    font-weight: 700;
}

.find-school-input,
.find-school-select {
    border: 1px solid #666;
    color: #333;
    display: inline-block;
    font-size: 14px;
    line-height: 1.2;
    margin-right: 2px;
    padding: 3px 6px;
}

.find-school-input {
    width: 200px;
}

.find-school-select {
    min-width: 268px;
}

.find-school-submit {
    background: linear-gradient(#fff, #e8f2f8);
    border: 1px solid #1a5d7f;
    border-radius: 5px;
    color: #1a5d7f;
    cursor: pointer;
    display: inline-block;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.2;
    padding: 5px 10px;
    white-space: nowrap;
    width: auto;
}

.find-school-submit:hover,
.find-school-submit:focus {
    background: linear-gradient(#eef8ff, #d6ebfa);
}

.find-school-browse {
    padding: 60px 0 0;
}

.find-school-browse-heading {
    padding: 20px 0 !important;
}

.find-school-tabs {
    border-bottom: 1px solid #ddd;
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
}

.find-school-tab-item {
    margin-bottom: -1px;
}

.find-school-tab {
    background: #fff;
    border: 1px solid transparent;
    border-radius: 4px 4px 0 0;
    color: #004f6e;
    cursor: pointer;
    display: block;
    font-size: 14px;
    line-height: 1.4;
    margin: 0;
    padding: 10px 15px;
}

.find-school-tab[aria-selected="true"] {
    background: #f6f7f9;
    border-color: #ddd #ddd transparent;
    color: #555;
}

.find-school-tab:hover {
    background-color: #eee;
}

.find-school-tab:focus {
    outline: 0;
}

.find-school-tab-panel {
    background: #f6f7f9;
    border: 1px solid #ddd;
    border-top: 0;
    border-radius: 0 0 4px 4px;
    padding: 20px;
}

.find-school-tab-panel-selected-category {
    padding: 0;
}

.find-school-tab-description {
    color: #333;
    display: block;
    font-size: 14px;
    margin: 14px 0;
}

.find-school-selected-state {
    display: inline-flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    margin: 0;
}

.find-school-state-tag {
    background: #143949;
    border-radius: 14px;
    color: #fff;
    display: inline-block;
    font-size: 13px;
    font-weight: 400;
    margin: 4px 2px;
    padding: 4px 10px;
}

.find-school-category-trail {
    border-bottom: 1px solid #ddd;
    padding: 20px;
}

.find-school-category-trail-title {
    color: #143949;
    font-weight: 600;
}

.find-school-category-trail-link {
    color: inherit;
    cursor: pointer;
    text-decoration: underline;
}

.find-school-category-trail-link:hover,
.find-school-category-trail-link:focus {
    color: inherit;
    font-weight: 400;
    text-decoration: underline;
}

.find-school-category-trail-separator {
    padding: 0 3px;
}

.find-school-category-results {
    padding: 20px;
}

.find-school-loading {
    align-items: center;
    color: #333;
    display: flex;
    gap: 12px;
    justify-content: center;
    min-height: 120px;
    padding: 24px;
}

.find-school-loading-spinner {
    animation: find-school-spinner 0.75s linear infinite;
    border: 4px solid #d8d8d8;
    border-radius: 50%;
    border-top-color: #005e82;
    box-sizing: border-box;
    height: 34px;
    width: 34px;
}

.find-school-loading-text,
.find-school-error {
    font-size: 14px;
}

.find-school-category-background-progress {
    background-color: gray;
    height: 100%;
    left: 0;
    opacity: .75;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 16777270;
}

.find-school-category-update-progress {
    background-color: #fff;
    border-radius: 10px;
    left: 50%;
    margin-left: -100px;
    margin-top: -50px;
    padding: 10px;
    position: fixed;
    text-align: center;
    top: 50%;
    z-index: 16777271;
}

.find-school-category-update-progress img {
    width: auto;
}

.find-school-error {
    color: #7c2d12;
    padding: 20px;
}

@keyframes find-school-spinner {
    to {
        transform: rotate(360deg);
    }
}

.find-school-state-list {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    line-height: 1.4;
    margin: 0;
}

.find-school-state-link {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    padding: 5px;
    width: 20%;
}

.find-school-state-link a,
.find-school-category-link {
    color: #004f6e;
    font-size: 14px;
    margin-right: auto;
    padding-bottom: 4px;
    text-decoration: underline;
}

.find-school-category-link {
    align-self: flex-start;
}

.find-school-state-link a:hover,
.find-school-category-link:hover {
    color: #004f6e;
    text-decoration: none;
}

.find-school-state-link a:focus,
.find-school-category-link:focus {
    outline: 5px auto -webkit-focus-ring-color;
    outline-offset: -2px;
}

.find-school-category-list {
    column-count: 4;
    column-gap: 0;
    margin: 0;
    padding: 20px 0 0;
}

.find-school-category-group {
    break-inside: avoid;
    display: flex;
    flex-direction: column;
    padding: 5px;
}

.find-school-category-title {
    color: #545454;
    font-size: 14px;
    font-weight: 700;
    padding-bottom: 10px;
}

.find-school-results-shell {
    padding-top: 18px;
}

.find-school-results-description {
    color: #333;
    display: block;
    font-size: 16px;
    line-height: 1.5;
    margin: 26px 0 28px;
    max-width: 1060px;
}

.find-school-results-description a {
    color: #2678c7;
    text-decoration: none;
}

.find-school-results-frame {
    background: #f6f7f9;
    border: 1px solid #ddd;
    border-radius: 4px;
    display: flex;
    flex-wrap: wrap;
    gap: 0;
    padding: 14px;
}

.find-school-pagination {
    align-items: center;
    background: #dedede;
    box-sizing: border-box;
    color: #333;
    display: flex;
    flex: 0 0 100%;
    font-size: 12px;
    justify-content: space-between;
    line-height: 1.4;
    margin: 8px -6px -14px;
    min-height: 42px;
    padding: 8px 12px;
}

.find-school-page-results {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.find-school-page-size {
    border: 1px solid #777;
    color: #333;
    font-size: 12px;
    height: 22px;
    padding: 1px 4px;
}

.find-school-page-nav {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    justify-content: flex-end;
}

.find-school-page-count {
    margin-right: 10px;
}

.find-school-page-link,
.find-school-page-link-placeholder {
    align-items: center;
    background: #f7f7f7;
    border: 1px solid #8f9ba4;
    box-sizing: border-box;
    color: #2678c7;
    display: inline-flex;
    font-size: 12px;
    height: 20px;
    justify-content: center;
    min-width: 18px;
    padding: 0 4px;
    text-decoration: none;
}

.find-school-page-link:hover,
.find-school-page-link:focus {
    background: #fff;
    text-decoration: underline;
}

.find-school-page-link-active {
    background: #fff;
    border-color: #2678c7;
    color: #333;
    font-weight: 700;
}

.find-school-page-link-placeholder {
    color: transparent;
    cursor: default;
}

.find-school-sr-only {
    clip: rect(0, 0, 0, 0);
    border: 0;
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
}

.find-school-result-card {
    box-sizing: border-box;
    display: flex;
    padding: 0 8px;
    width: 33.333%;
    word-wrap: break-word;
}

.find-school-result-frame {
    background: #24b776;
    display: flex;
    margin: 8px 0;
    width: 100%;
}

.find-school-result-card-inner {
    background: #fff;
    border: 1px solid #ccc;
    border-left: 0;
    box-sizing: border-box;
    color: #333;
    flex: 1 1 auto;
    font-size: 14px;
    line-height: 1.4;
    margin-left: 10px;
    min-height: 236px;
    padding: 14px;
}

.find-school-result-city {
    display: block;
    font-weight: 700;
    margin: 0 0 10px;
}

.find-school-result-name,
.find-school-result-name-link {
    color: #2678c7;
    display: inline-block;
    line-height: 1.4;
    text-decoration: none;
}

.find-school-result-name-link:hover,
.find-school-result-name-link:focus {
    text-decoration: underline;
}

.find-school-result-contact {
    margin-top: 0;
}

.find-school-result-contact-line {
    display: block;
}

.find-school-result-email {
    color: #2678c7;
    display: inline-block;
    font-style: italic;
    overflow-wrap: anywhere;
    text-decoration: none;
}

.find-school-result-distance {
    display: block;
}

.find-school-error {
    color: #c33;
    font-size: 16px;
    margin: 24px 0 0;
}

@media (max-width: 991px) {
    .find-school-category-list {
        column-count: 2;
    }

    .find-school-state-link {
        width: 33%;
    }

    .find-school-result-card {
        width: 50%;
    }

    .find-school-pagination {
        align-items: flex-start;
        flex-direction: column;
        gap: 8px;
    }

    .find-school-page-nav {
        justify-content: flex-start;
    }
}

@media (max-width: 768px) {
    .find-school-name-search {
        min-height: 196px;
        padding: 46px 16px 44px;
    }

    .find-school-name-search-field {
        height: 56px;
    }

    .find-school-name-search-input {
        font-size: 14px;
    }

    .find-school-name-result-link {
        align-items: flex-start;
        flex-direction: column;
        gap: 6px;
    }

    .find-school-locator {
        flex-direction: column;
        min-width: 0;
    }

    .find-school-locator-column {
        padding: 20px 0;
        text-align: center;
    }

    .find-school-locator-column + .find-school-locator-column {
        border-left: 0;
        border-top: 1px solid #ddd;
    }

    .find-school-tab-item {
        flex: 1 1 50%;
    }

    .find-school-tab {
        text-align: center;
        width: 100%;
    }
}

@media (max-width: 639px) {
    .find-school-heading,
    .find-school-browse-heading {
        font-size: 24px;
    }

    .find-school-input,
    .find-school-select {
        max-width: 100%;
        width: 100%;
    }

    .find-school-state-link {
        width: 100%;
    }

    .find-school-category-list {
        column-count: 1;
    }

    .find-school-result-card {
        width: 100%;
    }
}
`;

const normalizePositiveInteger = (value: number | undefined, fallback: number): number => {
    return value && value > 0 ? Math.floor(value) : fallback;
};

const normalizePageSize = (value: number | undefined): number => {
    return value && pageSizes.indexOf(value) >= 0 ? value : defaultPageSize;
};

const addQueryParam = (params: string[], name: string, value: string | number | undefined): void => {
    const normalizedValue = `${value ?? ''}`.trim();
    if (normalizedValue) {
        params.push(`${encodeURIComponent(name)}=${encodeURIComponent(normalizedValue)}`);
    }
};

const buildFindASchoolUrl = (
    productCode: string,
    returnUrl: string,
    zipCode: string,
    province: string,
    currentPage: number,
    pageSize: number,
): string => {
    const params: string[] = [];
    addQueryParam(params, 'product', productCode);
    addQueryParam(params, 'returnUrl', returnUrl);
    addQueryParam(params, 'Zip', zipCode);
    addQueryParam(params, 'Province', zipCode ? '' : province);
    addQueryParam(params, 'CurrPage', currentPage > 1 ? currentPage : undefined);
    addQueryParam(params, 'PageSize', pageSize !== defaultPageSize ? pageSize : undefined);

    return `/online-schools/${params.length ? `?${params.join('&')}` : ''}`;
};

const buildFindASchoolCategoryUrl = (
    productCode: string,
    returnUrl: string,
    categoryId: number,
    categoryState?: string,
): string => {
    const params: string[] = [];
    addQueryParam(params, 'product', productCode);
    addQueryParam(params, 'returnUrl', returnUrl);
    addQueryParam(params, 'category', categoryId);
    addQueryParam(params, 'categoryState', categoryState);

    return `/online-schools/${params.length ? `?${params.join('&')}` : ''}`;
};

const buildFindASchoolCategoryRootUrl = (productCode: string, returnUrl: string): string => {
    const params: string[] = [];
    addQueryParam(params, 'product', productCode);
    addQueryParam(params, 'returnUrl', returnUrl);
    addQueryParam(params, 'browse', 'category');

    return `/online-schools/${params.length ? `?${params.join('&')}` : ''}`;
};

const cleanFindASchoolBrowseQueryParams = (): void => {
    if (typeof window === 'undefined' || !window.history?.replaceState) {
        return;
    }

    const currentUrl = new URL(window.location.href);
    const originalSearch = currentUrl.search;

    currentUrl.searchParams.delete('category');
    currentUrl.searchParams.delete('categoryState');
    currentUrl.searchParams.delete('browse');

    if (currentUrl.search !== originalSearch) {
        window.history.replaceState(
            window.history.state,
            document.title,
            `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
        );
    }
};

const findCategoryById = (categoryGroups: CategoryGroup[], categoryId?: number): CategoryLink | null => {
    if (!categoryId) {
        return null;
    }

    return (
        categoryGroups
            .flatMap((group) => group.subCategories)
            .find((category) => category.subCategoryId === categoryId) || null
    );
};

const buildPageNumbers = (currentPage: number, totalPages: number): number[] => {
    if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const startPage = Math.max(Math.min(currentPage - 2, totalPages - 4), 1);
    return Array.from({ length: 5 }, (_, index) => startPage + index);
};

const isPartnerAutocompleteResult = (result: FindASchoolAutocompleteResult): boolean => {
    return result.type === 'PartnerOIC' || result.type === 'PartnerCTP';
};

const getPartnerBadgeLabel = (result: FindASchoolAutocompleteResult): string => {
    return result.type === 'PartnerCTP' ? 'Advanced Career Training' : 'Fundamentals';
};

const getPartnerBadgeClassName = (result: FindASchoolAutocompleteResult): string => {
    return result.type === 'PartnerCTP'
        ? 'find-school-name-result-badge find-school-name-result-badge-career-training'
        : 'find-school-name-result-badge find-school-name-result-badge-fundamentals';
};

interface FindASchoolNameSearchProps {
    autocompleteApiPath: string;
}

const FindASchoolNameSearch: React.FC<FindASchoolNameSearchProps> = ({ autocompleteApiPath }) => {
    const [inputValue, setInputValue] = React.useState('');
    const [results, setResults] = React.useState<FindASchoolAutocompleteResult[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');
    const searchTerm = inputValue.trim();
    const partnerResults = results.filter(isPartnerAutocompleteResult);
    const locationResults = results.filter((result) => result.type === 'Location');
    const shouldShowResults =
        searchTerm.length >= 2 &&
        (isLoading || Boolean(errorMessage) || partnerResults.length > 0 || locationResults.length > 0);

    React.useEffect(() => {
        if (searchTerm.length < 2) {
            setResults([]);
            setIsLoading(false);
            setErrorMessage('');
            return undefined;
        }

        setIsLoading(true);
        setErrorMessage('');

        const request = xhrClient.getFindASchoolAutocompleteResult(autocompleteApiPath, searchTerm);
        let isCurrentRequest = true;

        const loadResults = async (): Promise<void> => {
            try {
                const response = await request.promise;
                if (isCurrentRequest) {
                    setResults(response.data);
                }
            } catch (error) {
                if (!request.isCancel(error) && isCurrentRequest) {
                    setResults([]);
                    setErrorMessage('Unable to load schools right now.');
                }
            } finally {
                if (isCurrentRequest) {
                    setIsLoading(false);
                }
            }
        };

        loadResults();

        return () => {
            isCurrentRequest = false;
            request.cancel();
        };
    }, [autocompleteApiPath, searchTerm]);

    const renderResultItem = (result: FindASchoolAutocompleteResult) => (
        <li className="find-school-name-result-item" key={`${result.type}-${result.name}-${result.url}`}>
            <a className="find-school-name-result-link" href={result.url}>
                <span className="find-school-name-result-label">{result.name}</span>
                {isPartnerAutocompleteResult(result) && (
                    <span className={getPartnerBadgeClassName(result)}>{getPartnerBadgeLabel(result)}</span>
                )}
            </a>
        </li>
    );

    const renderResultGroup = (heading: string, groupResults: FindASchoolAutocompleteResult[]) => {
        if (!groupResults.length) {
            return null;
        }

        return (
            <>
                <li className="find-school-name-results-header">{heading}</li>
                {groupResults.map(renderResultItem)}
            </>
        );
    };

    return (
        <section className="find-school-name-search">
            <div className="find-school-name-search-inner">
                <p className="find-school-name-search-prompt">
                    <strong>Have a school in mind?</strong> Start typing in the box below.
                </p>
                <div className="find-school-name-search-field">
                    <span className="find-school-name-search-icon" aria-hidden="true" />
                    <input
                        aria-autocomplete="list"
                        aria-controls="find-school-name-results"
                        aria-expanded={shouldShowResults}
                        aria-label="School name or city"
                        className="find-school-name-search-input"
                        onChange={(event) => setInputValue(event.currentTarget.value)}
                        placeholder="Enter a school name or city here..."
                        role="combobox"
                        type="text"
                        value={inputValue}
                    />
                </div>
                {shouldShowResults && (
                    <ul className="find-school-name-results" id="find-school-name-results">
                        {renderResultGroup('Partners', partnerResults)}
                        {renderResultGroup('Locations', locationResults)}
                        {isLoading && !results.length && (
                            <li className="find-school-name-status">Loading schools...</li>
                        )}
                        {errorMessage && <li className="find-school-name-status">{errorMessage}</li>}
                    </ul>
                )}
            </div>
        </section>
    );
};

const renderContextFields = (productCode: string, returnUrl: string) => (
    <>
        {productCode && <input name="product" type="hidden" value={productCode} />}
        {returnUrl && <input name="returnUrl" type="hidden" value={returnUrl} />}
    </>
);

const buildCityStateZip = (school: SchoolResult): string => {
    const city = school.city?.trim();
    const stateZip = [school.state, school.zipCode].filter(Boolean).join(' ').trim();

    if (city && stateZip) {
        return `${city}, ${stateZip}`;
    }

    return city || stateZip;
};

const buildPhone = (school: SchoolResult): string => {
    if (!school.phoneNumber) {
        return '';
    }

    return school.phoneNumberExtension
        ? `${school.phoneNumber} ext. ${school.phoneNumberExtension}`
        : school.phoneNumber;
};

const buildSchoolUrl = (school: SchoolResult, productCode: string, returnUrl: string): string => {
    if (productCode && returnUrl) {
        return `${returnUrl}?id=${encodeURIComponent(productCode)}&site=${encodeURIComponent(school.siteCode)}`;
    }

    return school.siteUrl || '';
};

const renderSchoolName = (school: SchoolResult, productCode: string, returnUrl: string) => {
    const schoolUrl = buildSchoolUrl(school, productCode, returnUrl);
    const lines = [school.siteName, school.siteSubTitle].filter((line): line is string => Boolean(line));

    if (!schoolUrl) {
        return (
            <span className="find-school-result-name">
                {lines.map((line, index) => (
                    <React.Fragment key={`${line}-${index}`}>
                        {index > 0 && <br />}
                        {line}
                    </React.Fragment>
                ))}
            </span>
        );
    }

    return (
        <a
            className="find-school-result-name-link"
            href={schoolUrl}
            target={productCode ? '_self' : '_blank'}
            rel={productCode ? undefined : 'noreferrer'}
        >
            {lines.map((line, index) => (
                <React.Fragment key={`${line}-${index}`}>
                    {index > 0 && <br />}
                    {line}
                </React.Fragment>
            ))}
        </a>
    );
};

const renderSchoolCard = (school: SchoolResult, productCode: string, returnUrl: string) => {
    const cityStateZip = buildCityStateZip(school);
    const phone = buildPhone(school);

    return (
        <article className="find-school-result-card" key={`${school.siteId}-${school.siteCode}`}>
            <div className="find-school-result-frame">
                <div className="find-school-result-card-inner">
                    {school.isSearchByProvince && school.city && (
                        <span className="find-school-result-city">{school.city}</span>
                    )}
                    {renderSchoolName(school, productCode, returnUrl)}
                    <div className="find-school-result-contact">
                        {school.address1 && <span className="find-school-result-contact-line">{school.address1}</span>}
                        {school.address2 && <span className="find-school-result-contact-line">{school.address2}</span>}
                        {cityStateZip && <span className="find-school-result-contact-line">{cityStateZip}</span>}
                        {phone && <span className="find-school-result-contact-line">Ph: {phone}</span>}
                    </div>
                    {school.emailAddress && (
                        <a className="find-school-result-email" href={`mailto:${school.emailAddress}`}>
                            {school.emailAddress}
                        </a>
                    )}
                    {school.isSearchByZip && school.distanceFromZip && (
                        <span className="find-school-result-distance">[{school.distanceFromZip} miles]</span>
                    )}
                </div>
            </div>
        </article>
    );
};

const renderNavLink = (
    label: string,
    title: string,
    targetPage: number,
    currentPage: number,
    productCode: string,
    returnUrl: string,
    zipCode: string,
    province: string,
    pageSize: number,
    isPageNumber = false,
) => {
    if (targetPage === currentPage) {
        return (
            <span
                className="find-school-page-link find-school-page-link-active"
                key={`${title}-${label}-${targetPage}`}
            >
                {label}
            </span>
        );
    }

    return (
        <a
            aria-label={title}
            className="find-school-page-link"
            href={buildFindASchoolUrl(productCode, returnUrl, zipCode, province, targetPage, pageSize)}
            key={`${title}-${label}-${targetPage}`}
            title={isPageNumber ? undefined : title}
        >
            {isPageNumber ? label : <span className="find-school-sr-only">{title}</span>}
        </a>
    );
};

const renderPagination = (
    searchResults: SearchResults,
    productCode: string,
    returnUrl: string,
    fallbackZipCode: string,
    fallbackProvince: string,
) => {
    const totalResults = normalizePositiveInteger(searchResults.virtualCount, searchResults.schools.length);
    const pageSize = normalizePageSize(searchResults.pageSize);
    const totalPages = Math.max(Math.ceil(totalResults / pageSize), 1);
    const currentPage = Math.min(normalizePositiveInteger(searchResults.currentPage, 1), totalPages);
    const zipCode = (searchResults.zipCode || fallbackZipCode).trim();
    const province = (searchResults.province || fallbackProvince).trim();

    if (!searchResults.isSearchByProvince || totalResults <= defaultPageSize) {
        return null;
    }

    const pageSizeUrl = (size: number) => buildFindASchoolUrl(productCode, returnUrl, zipCode, province, 1, size);

    return (
        <div className="find-school-pagination">
            <div className="find-school-page-results">
                <label htmlFor="find-school-page-size">Results per page:</label>
                <select
                    className="find-school-page-size"
                    id="find-school-page-size"
                    onChange={(event) => {
                        window.location.href = pageSizeUrl(Number(event.currentTarget.value));
                    }}
                    value={pageSize}
                >
                    {pageSizes.map((size) => (
                        <option key={size} value={size}>
                            {size}
                        </option>
                    ))}
                </select>
                <span>Total Results: {totalResults}</span>
            </div>
            <nav className="find-school-page-nav" aria-label="Find a School results pages">
                <span className="find-school-page-count">
                    Page: {currentPage} of {totalPages}
                </span>
                {currentPage > 1 &&
                    renderNavLink(
                        '',
                        'First page',
                        1,
                        currentPage,
                        productCode,
                        returnUrl,
                        zipCode,
                        province,
                        pageSize,
                    )}
                {currentPage > 1 &&
                    renderNavLink(
                        '',
                        'Previous page',
                        currentPage - 1,
                        currentPage,
                        productCode,
                        returnUrl,
                        zipCode,
                        province,
                        pageSize,
                    )}
                {buildPageNumbers(currentPage, totalPages).map((pageNumber) =>
                    renderNavLink(
                        pageNumber.toString(),
                        `Page ${pageNumber}`,
                        pageNumber,
                        currentPage,
                        productCode,
                        returnUrl,
                        zipCode,
                        province,
                        pageSize,
                        true,
                    ),
                )}
                {currentPage < totalPages &&
                    renderNavLink(
                        '',
                        'Next page',
                        currentPage + 1,
                        currentPage,
                        productCode,
                        returnUrl,
                        zipCode,
                        province,
                        pageSize,
                    )}
                {currentPage < totalPages &&
                    renderNavLink(
                        '',
                        'Last page',
                        totalPages,
                        currentPage,
                        productCode,
                        returnUrl,
                        zipCode,
                        province,
                        pageSize,
                    )}
            </nav>
        </div>
    );
};

interface BrowseTabButtonsProps {
    activeTab: BrowseTab;
    onTabChange: (tab: BrowseTab) => void;
}

interface BrowseLocationPanelProps {
    isActive: boolean;
    links: LocationLink[];
    selectedStateName: string;
}

interface CategoryTrailProps {
    onCategoryClear: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    onCategoryStateClear: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    productCode: string;
    returnUrl: string;
    selectedCategoryId: number;
    selectedCategoryName: string;
    selectedCategoryStateName: string;
}

interface BrowseCategoryPanelProps {
    categoryGroupsFromProps: CategoryGroup[];
    hasSelectedCategory: boolean;
    isActive: boolean;
    links: LocationLink[];
    onCategoryClear: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    onCategorySelect: (category: CategoryLink, event: React.MouseEvent<HTMLAnchorElement>) => void;
    onCategoryStateClear: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    onCategoryStateSelect: CategoryBrowseLinkClickHandler;
    productCode: string;
    returnUrl: string;
    selectedCategoryId: number;
    selectedCategoryName: string;
    selectedCategoryStateName: string;
}

interface FindASchoolBrowseProps {
    browseTab?: BrowseTab;
    categoryCityLinks: LocationLink[];
    categoryGroupsFromProps: CategoryGroup[];
    categoryCitiesApiPath: string;
    categoryStateLinks: StateLink[];
    categoryStatesApiPath: string;
    cityLinks: LocationLink[];
    productCode: string;
    returnUrl: string;
    selectedCategoryId?: number;
    selectedCategoryName: string;
    selectedCategoryStateName: string;
    selectedStateName: string;
    stateLinks: StateLink[];
}

const renderLocationLinkList = (links: LocationLink[], onLinkClick?: CategoryBrowseLinkClickHandler) => (
    <div className="find-school-state-list">
        {links.map((link) => (
            <div className="find-school-state-link" key={`${link.url}-${link.name}`}>
                <a href={link.url} onClick={onLinkClick ? (event) => onLinkClick(link, event) : undefined}>
                    {link.name}
                </a>
            </div>
        ))}
    </div>
);

const buildBrowseLocationLinks = (
    selectedStateName: string,
    cityLinks: LocationLink[],
    stateLinks: StateLink[],
): LocationLink[] => {
    if (selectedStateName.trim().length > 0 && cityLinks.length) {
        return cityLinks;
    }

    return stateLinks.map((state) => ({
        name: state.name,
        url: state.url || `/location/us/${state.slug}/`,
    }));
};

const buildCategoryLocationLinks = (
    productCode: string,
    returnUrl: string,
    selectedCategoryId: number,
    selectedCategoryStateName: string,
    categoryStateLinks: StateLink[],
    categoryCityLinks: LocationLink[],
): LocationLink[] => {
    if (selectedCategoryStateName.trim().length > 0) {
        return categoryCityLinks;
    }

    return categoryStateLinks.map((state) => ({
        name: state.name,
        url: buildFindASchoolCategoryUrl(productCode, returnUrl, selectedCategoryId, state.abbreviation),
        abbreviation: state.abbreviation,
        slug: state.slug,
    }));
};

const isStandardBrowseClick = (event: React.MouseEvent<HTMLAnchorElement>): boolean => {
    return event.button === 0 && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
};

const CategoryBrowseLoading: React.FC = () => (
    <>
        <div className="find-school-category-background-progress" />
        <div aria-live="assertive" className="find-school-category-update-progress" role="status">
            <img alt="" src="/assets/image/ajax-loader.gif" />
            <br />
            <strong>
                <span>Loading, please wait...</span>
            </strong>
            <br />
        </div>
    </>
);

const BrowseTabButtons: React.FC<BrowseTabButtonsProps> = ({ activeTab, onTabChange }) => (
    <ul className="find-school-tabs" role="tablist">
        <li className="find-school-tab-item" role="presentation">
            <button
                aria-controls="find-school-location-panel"
                aria-selected={activeTab === 'location'}
                className="find-school-tab"
                id="find-school-location-tab"
                onClick={() => onTabChange('location')}
                role="tab"
                type="button"
            >
                Browse by Location
            </button>
        </li>
        <li className="find-school-tab-item" role="presentation">
            <button
                aria-controls="find-school-category-panel"
                aria-selected={activeTab === 'category'}
                className="find-school-tab"
                id="find-school-category-tab"
                onClick={() => onTabChange('category')}
                role="tab"
                type="button"
            >
                Browse by Category
            </button>
        </li>
    </ul>
);

const BrowseLocationPanel: React.FC<BrowseLocationPanelProps> = ({ isActive, links, selectedStateName }) => {
    if (!isActive) {
        return null;
    }

    return (
        <div
            aria-labelledby="find-school-location-tab"
            className="find-school-tab-panel"
            id="find-school-location-panel"
            role="tabpanel"
        >
            {selectedStateName.trim().length > 0 ? (
                <div className="find-school-selected-state">
                    <span className="find-school-state-tag">{selectedStateName}</span>
                </div>
            ) : (
                <span className="find-school-tab-description">
                    Currently available in the United States only. Please choose a State below.
                </span>
            )}
            {renderLocationLinkList(links)}
        </div>
    );
};

const CategoryTrail: React.FC<CategoryTrailProps> = ({
    onCategoryClear,
    onCategoryStateClear,
    productCode,
    returnUrl,
    selectedCategoryId,
    selectedCategoryName,
    selectedCategoryStateName,
}) => (
    <div className="find-school-category-trail">
        <span className="find-school-category-trail-title">
            <a
                className="find-school-category-trail-link"
                href={buildFindASchoolCategoryRootUrl(productCode, returnUrl)}
                onClick={onCategoryClear}
                title="Click to remove"
            >
                {selectedCategoryName}
            </a>
            {selectedCategoryStateName.trim().length > 0 && (
                <>
                    <span className="find-school-category-trail-separator"> &gt; </span>
                    <a
                        className="find-school-category-trail-link"
                        href={buildFindASchoolCategoryUrl(productCode, returnUrl, selectedCategoryId)}
                        onClick={onCategoryStateClear}
                        title="Click to remove"
                    >
                        {selectedCategoryStateName}
                    </a>
                </>
            )}
        </span>
    </div>
);

const BrowseCategoryChoices: React.FC<
    Pick<BrowseCategoryPanelProps, 'categoryGroupsFromProps' | 'onCategorySelect' | 'productCode' | 'returnUrl'>
> = ({ categoryGroupsFromProps, onCategorySelect, productCode, returnUrl }) => (
    <div className="find-school-category-list">
        {categoryGroupsFromProps.map((group) => (
            <div className="find-school-category-group" key={group.parentTreeNodeId}>
                <span className="find-school-category-title">{group.parentCategory}</span>
                {group.subCategories.map((category) => (
                    <a
                        className="find-school-category-link"
                        href={buildFindASchoolCategoryUrl(productCode, returnUrl, category.subCategoryId)}
                        key={category.subCategoryId}
                        onClick={(event) => onCategorySelect(category, event)}
                    >
                        {category.subCategoryName}
                    </a>
                ))}
            </div>
        ))}
    </div>
);

const BrowseCategoryPanel: React.FC<BrowseCategoryPanelProps> = ({
    categoryGroupsFromProps,
    hasSelectedCategory,
    isActive,
    links,
    onCategoryClear,
    onCategorySelect,
    onCategoryStateClear,
    onCategoryStateSelect,
    productCode,
    returnUrl,
    selectedCategoryId,
    selectedCategoryName,
    selectedCategoryStateName,
}) => {
    if (!isActive) {
        return null;
    }

    return (
        <div
            aria-labelledby="find-school-category-tab"
            className={`find-school-tab-panel${hasSelectedCategory ? ' find-school-tab-panel-selected-category' : ''}`}
            id="find-school-category-panel"
            role="tabpanel"
        >
            {hasSelectedCategory ? (
                <>
                    <CategoryTrail
                        onCategoryClear={onCategoryClear}
                        onCategoryStateClear={onCategoryStateClear}
                        productCode={productCode}
                        returnUrl={returnUrl}
                        selectedCategoryId={selectedCategoryId}
                        selectedCategoryName={selectedCategoryName}
                        selectedCategoryStateName={selectedCategoryStateName}
                    />
                    <div className="find-school-category-results">
                        {renderLocationLinkList(
                            links,
                            selectedCategoryStateName.trim().length > 0 ? undefined : onCategoryStateSelect,
                        )}
                    </div>
                </>
            ) : (
                <BrowseCategoryChoices
                    categoryGroupsFromProps={categoryGroupsFromProps}
                    onCategorySelect={onCategorySelect}
                    productCode={productCode}
                    returnUrl={returnUrl}
                />
            )}
        </div>
    );
};

const FindASchoolBrowse: React.FC<FindASchoolBrowseProps> = ({
    browseTab,
    categoryCityLinks,
    categoryCitiesApiPath,
    categoryGroupsFromProps,
    categoryStateLinks,
    categoryStatesApiPath,
    cityLinks,
    productCode,
    returnUrl,
    selectedCategoryId,
    selectedCategoryName,
    selectedCategoryStateName,
    selectedStateName,
    stateLinks,
}) => {
    const initialSelectedCategory =
        findCategoryById(categoryGroupsFromProps, selectedCategoryId) ||
        (selectedCategoryId && selectedCategoryName.trim()
            ? {
                  subCategoryId: selectedCategoryId,
                  subCategoryCode: '',
                  subCategoryName: selectedCategoryName.trim(),
              }
            : null);
    const [activeCategory, setActiveCategory] = React.useState<CategoryLink | null>(initialSelectedCategory);
    const [activeCategoryStateName, setActiveCategoryStateName] = React.useState(selectedCategoryStateName);
    const [activeCategoryStateLinks, setActiveCategoryStateLinks] = React.useState<StateLink[]>(categoryStateLinks);
    const [activeCategoryCityLinks, setActiveCategoryCityLinks] = React.useState<LocationLink[]>(categoryCityLinks);
    const hasSelectedCategory = Boolean(activeCategory);
    const selectedCategoryIdValue = activeCategory?.subCategoryId || 0;
    const selectedCategoryNameValue = activeCategory?.subCategoryName || '';
    const defaultBrowseTab = hasSelectedCategory || browseTab === 'category' ? 'category' : 'location';
    const [activeTab, setActiveTab] = React.useState<BrowseTab>(defaultBrowseTab);
    const [isCategoryBrowseLoading, setIsCategoryBrowseLoading] = React.useState(false);
    const browseStateLinks = stateLinks;
    const browseCategoryGroups = categoryGroupsFromProps;
    const locationLinks = buildBrowseLocationLinks(selectedStateName, cityLinks, browseStateLinks);
    const categoryLocationLinks = buildCategoryLocationLinks(
        productCode,
        returnUrl,
        selectedCategoryIdValue,
        activeCategoryStateName,
        activeCategoryStateLinks,
        activeCategoryCityLinks,
    );
    React.useEffect(() => {
        cleanFindASchoolBrowseQueryParams();
    }, []);

    const navigateToFallbackUrl = React.useCallback((targetUrl: string) => {
        window.location.assign(targetUrl);
    }, []);

    const handleCategorySelect = React.useCallback(
        (category: CategoryLink, event: React.MouseEvent<HTMLAnchorElement>) => {
            if (!isStandardBrowseClick(event)) {
                return;
            }

            const fallbackUrl = event.currentTarget.href;

            event.preventDefault();
            setActiveTab('category');
            setIsCategoryBrowseLoading(true);

            const request = xhrClient.getFindASchoolCategoryStateLinks(categoryStatesApiPath, category.subCategoryId);

            void (async () => {
                try {
                    const response = await request.promise;

                    cleanFindASchoolBrowseQueryParams();
                    setActiveCategory(category);
                    setActiveCategoryStateName('');
                    setActiveCategoryStateLinks(response.data);
                    setActiveCategoryCityLinks([]);
                } catch (error) {
                    if (!request.isCancel(error)) {
                        navigateToFallbackUrl(fallbackUrl);
                    }
                } finally {
                    setIsCategoryBrowseLoading(false);
                }
            })();
        },
        [categoryStatesApiPath, navigateToFallbackUrl],
    );

    const handleCategoryStateSelect = React.useCallback(
        (link: LocationLink, event: React.MouseEvent<HTMLAnchorElement>) => {
            if (!isStandardBrowseClick(event) || !activeCategory || !link.abbreviation) {
                return;
            }

            const fallbackUrl = event.currentTarget.href;

            event.preventDefault();
            setActiveTab('category');
            setIsCategoryBrowseLoading(true);

            const request = xhrClient.getFindASchoolCategoryCityLinks(
                categoryCitiesApiPath,
                activeCategory.subCategoryId,
                link.abbreviation,
            );

            void (async () => {
                try {
                    const response = await request.promise;

                    cleanFindASchoolBrowseQueryParams();
                    setActiveCategoryStateName(link.name);
                    setActiveCategoryCityLinks(response.data);
                } catch (error) {
                    if (!request.isCancel(error)) {
                        navigateToFallbackUrl(fallbackUrl);
                    }
                } finally {
                    setIsCategoryBrowseLoading(false);
                }
            })();
        },
        [activeCategory, categoryCitiesApiPath, navigateToFallbackUrl],
    );

    const handleCategoryClear = React.useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        setActiveTab('category');
        setIsCategoryBrowseLoading(true);

        window.setTimeout(() => {
            cleanFindASchoolBrowseQueryParams();
            setActiveCategory(null);
            setActiveCategoryStateName('');
            setActiveCategoryStateLinks([]);
            setActiveCategoryCityLinks([]);
            setIsCategoryBrowseLoading(false);
        }, categoryBrowseBackLoadingDelayMs);
    }, []);

    const handleCategoryStateClear = React.useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        setActiveTab('category');
        setIsCategoryBrowseLoading(true);

        window.setTimeout(() => {
            cleanFindASchoolBrowseQueryParams();
            setActiveCategoryStateName('');
            setActiveCategoryCityLinks([]);
            setIsCategoryBrowseLoading(false);
        }, categoryBrowseBackLoadingDelayMs);
    }, []);

    return (
        <section className="find-school-browse">
            <h2 className="find-school-browse-heading">Want to browse for a school?</h2>
            <BrowseTabButtons activeTab={activeTab} onTabChange={setActiveTab} />
            <BrowseLocationPanel
                isActive={activeTab === 'location'}
                links={locationLinks}
                selectedStateName={selectedStateName}
            />
            <BrowseCategoryPanel
                categoryGroupsFromProps={browseCategoryGroups}
                hasSelectedCategory={hasSelectedCategory}
                isActive={activeTab === 'category'}
                links={categoryLocationLinks}
                onCategoryClear={handleCategoryClear}
                onCategorySelect={handleCategorySelect}
                onCategoryStateClear={handleCategoryStateClear}
                onCategoryStateSelect={handleCategoryStateSelect}
                productCode={productCode}
                returnUrl={returnUrl}
                selectedCategoryId={selectedCategoryIdValue}
                selectedCategoryName={selectedCategoryNameValue}
                selectedCategoryStateName={activeCategoryStateName}
            />
            {isCategoryBrowseLoading && <CategoryBrowseLoading />}
        </section>
    );
};

const FindASchool: React.FC<FindASchoolProps> = ({
    productCode = '',
    returnUrl = '',
    showFindASchoolLocator = true,
    enableFindASchoolByName = false,
    autocompleteApiPath = '/location/autocomplete/schools',
    categoryStatesApiPath = '/location/browse/category-states',
    categoryCitiesApiPath = '/location/browse/category-cities',
    browseTab,
    provinceOptions = [],
    stateLinks = [],
    selectedStateName = '',
    cityLinks = [],
    categoryGroups: categoryGroupsFromProps = [],
    selectedCategoryId,
    selectedCategoryName = '',
    categoryStateLinks = [],
    selectedCategoryStateName = '',
    categoryCityLinks = [],
    zipCode = '',
    province = '',
    searchResults = null,
}) => {
    const normalizedProductCode = productCode.trim();
    const normalizedReturnUrl = returnUrl.trim();
    const normalizedSelectedStateName = selectedStateName.trim();
    const browseSection = (
        <FindASchoolBrowse
            browseTab={browseTab}
            categoryCityLinks={categoryCityLinks}
            categoryCitiesApiPath={categoryCitiesApiPath}
            categoryGroupsFromProps={categoryGroupsFromProps}
            categoryStateLinks={categoryStateLinks}
            categoryStatesApiPath={categoryStatesApiPath}
            cityLinks={cityLinks}
            productCode={normalizedProductCode}
            returnUrl={normalizedReturnUrl}
            selectedCategoryId={selectedCategoryId}
            selectedCategoryName={selectedCategoryName}
            selectedCategoryStateName={selectedCategoryStateName}
            selectedStateName={normalizedSelectedStateName}
            stateLinks={stateLinks}
        />
    );

    if (searchResults) {
        const hasResults = searchResults.schools.length > 0;

        return (
            <main className="find-school-page">
                <style>{styles}</style>
                <div className="find-school-shell find-school-results-shell">
                    <h1 className="find-school-heading">
                        Find a School - {hasResults ? 'Search Results' : 'No Search Results'}
                    </h1>
                    {hasResults ? (
                        <>
                            <p className="find-school-results-description">
                                Each of the schools listed below offers some or all of the titles in the{' '}
                                <a href="/courses">ed2go online course catalog</a>. Click on a school name to continue
                                the enrollment process at that school, or for other inquiries regarding enrollment in a
                                genuine ed2go online course, simply call or email the school of your choice using the
                                contact information provided.
                            </p>
                            <div className="find-school-results-frame">
                                {searchResults.schools.map((school) =>
                                    renderSchoolCard(school, normalizedProductCode, normalizedReturnUrl),
                                )}
                                {renderPagination(
                                    searchResults,
                                    normalizedProductCode,
                                    normalizedReturnUrl,
                                    zipCode,
                                    province,
                                )}
                            </div>
                            {browseSection}
                        </>
                    ) : (
                        <p className="find-school-error">
                            We&apos;re sorry, but your search produced no results. Please use your browser&apos;s Back
                            button and try a new search.
                        </p>
                    )}
                </div>
            </main>
        );
    }

    return (
        <main className="find-school-page">
            <style>{styles}</style>
            {enableFindASchoolByName ? (
                <FindASchoolNameSearch autocompleteApiPath={autocompleteApiPath} />
            ) : (
                <div className="find-school-breadcrumb-bar">
                    <nav className="find-school-breadcrumbs" aria-label="Breadcrumb">
                        <a href="/">ed2go</a>
                        <span className="find-school-breadcrumb-separator" aria-hidden="true">
                            &gt;
                        </span>
                        {normalizedSelectedStateName.length > 0 ? (
                            <>
                                <a href="/online-schools/">Find a School</a>
                                <span className="find-school-breadcrumb-separator" aria-hidden="true">
                                    &gt;
                                </span>
                                <span>{normalizedSelectedStateName}</span>
                            </>
                        ) : (
                            <span>Find a School</span>
                        )}
                    </nav>
                </div>
            )}
            <div className="find-school-shell">
                {showFindASchoolLocator && !enableFindASchoolByName && (
                    <section className="find-school-intro">
                        <h1 className="find-school-heading">Find a School</h1>
                        <p className="find-school-description">
                            To locate a school offering ed2go online courses near you, please enter your zip code or
                            select your province from the list below. Then, click the <strong>Locate a School</strong>{' '}
                            button.
                        </p>
                        <div className="find-school-locator">
                            <div className="find-school-locator-column">
                                <form action="/online-schools/" className="find-school-form" method="get">
                                    {renderContextFields(normalizedProductCode, normalizedReturnUrl)}
                                    <p className="find-school-audience">US Students:</p>
                                    <p>
                                        <label htmlFor="find-school-zip">Enter your zip code:</label>
                                    </p>
                                    <p>
                                        <input
                                            className="find-school-input"
                                            id="find-school-zip"
                                            inputMode="numeric"
                                            maxLength={5}
                                            name="Zip"
                                            pattern="[0-9]{5}"
                                            required
                                            type="text"
                                        />
                                    </p>
                                    <p>
                                        <button className="find-school-submit" type="submit">
                                            Locate A School
                                        </button>
                                    </p>
                                </form>
                            </div>
                            <div className="find-school-locator-column">
                                <form action="/online-schools/" className="find-school-form" method="get">
                                    {renderContextFields(normalizedProductCode, normalizedReturnUrl)}
                                    <p className="find-school-audience">Other Students:</p>
                                    <p>
                                        <label htmlFor="find-school-province">Select your province or country:</label>
                                    </p>
                                    <p>
                                        <select
                                            className="find-school-select"
                                            id="find-school-province"
                                            name="Province"
                                            required
                                        >
                                            <option value="">Select One</option>
                                            {provinceOptions.map((province) => (
                                                <option key={province.value} value={province.value}>
                                                    {province.label}
                                                </option>
                                            ))}
                                        </select>
                                    </p>
                                    <p>
                                        <button className="find-school-submit" type="submit">
                                            Locate A School
                                        </button>
                                    </p>
                                </form>
                            </div>
                        </div>
                    </section>
                )}
                {browseSection}
            </div>
        </main>
    );
};

export { FindASchool };
